/**
 * Review Status - Union type for mention review status
 *
 * Matches backend ReviewStatus values from:
 * Backend/MentionedMe.Domain/Entities/Reddit/RedditMention.cs
 */
export type ReviewStatus = 'Pending' | 'Approved' | 'Rejected' | 'Flagged' | 'Unreviewed' | '';

/**
 * Review status constants for use in UI
 */
export const REVIEW_STATUS = {
  PENDING: 'Pending' as const,
  APPROVED: 'Approved' as const,
  REJECTED: 'Rejected' as const,
  FLAGGED: 'Flagged' as const,
  UNREVIEWED: 'Unreviewed' as const, // Old mentions before review system (UI sentinel)
  EMPTY: '' as const, // Actual API value for unreviewed
  ALL: 'All' as const // For filter dropdown only
} as const;

/**
 * Review status display labels
 */
export const REVIEW_STATUS_LABELS: Record<ReviewStatus | 'All', string> = {
  [REVIEW_STATUS.PENDING]: 'Pending Review',
  [REVIEW_STATUS.APPROVED]: 'Approved',
  [REVIEW_STATUS.REJECTED]: 'Rejected',
  [REVIEW_STATUS.FLAGGED]: 'Flagged',
  [REVIEW_STATUS.UNREVIEWED]: 'Unreviewed',
  [REVIEW_STATUS.EMPTY]: 'Unreviewed',
  All: 'All Statuses'
};

/**
 * Review status badge colors (Mantine colors)
 */
export const REVIEW_STATUS_COLORS: Record<ReviewStatus | 'All', string> = {
  [REVIEW_STATUS.PENDING]: 'yellow',
  [REVIEW_STATUS.APPROVED]: 'green',
  [REVIEW_STATUS.REJECTED]: 'red',
  [REVIEW_STATUS.FLAGGED]: 'orange',
  [REVIEW_STATUS.UNREVIEWED]: 'gray',
  [REVIEW_STATUS.EMPTY]: 'gray',
  All: 'blue'
};

/**
 * Helper to check if status is valid
 */
export function isValidReviewStatus(status: string): status is ReviewStatus {
  return ['Pending', 'Approved', 'Rejected', 'Flagged', 'Unreviewed', ''].includes(status);
}

/**
 * Get display label for review status
 */
export function getReviewStatusLabel(status: ReviewStatus | 'All'): string {
  return REVIEW_STATUS_LABELS[status] || status;
}

/**
 * Get badge color for review status
 */
export function getReviewStatusColor(status: ReviewStatus | 'All'): string {
  return REVIEW_STATUS_COLORS[status] || 'gray';
}
